import { router, useForm, usePage } from "@inertiajs/react"
import { UserPlus, Trash2 } from "lucide-react"
import type { ReactNode } from "react"
import { useState } from "react"

import { AdminLayout } from "@/layouts/AdminLayout"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type {
  MembershipRole,
  PropertyMemberRow,
  PropertyOwnerRow,
  PropertySummary,
} from "@/lib/types"

interface Props {
  property: PropertySummary
  owner: PropertyOwnerRow
  members: PropertyMemberRow[]
  can_manage_team: boolean
  urls: { invite: string }
}

interface SharedProps {
  errors: Record<string, string>
}

// C3: SheetDescription added for Radix UI accessibility compliance
function InviteSheet({
  inviteUrl,
  open,
  onClose,
}: {
  inviteUrl: string
  open: boolean
  onClose: () => void
}) {
  const { errors } = usePage<SharedProps>().props
  const { data, setData, post, processing, reset } = useForm({
    email: "",
    role: "staff" as MembershipRole,
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(inviteUrl, {
      onSuccess: () => {
        reset()
        onClose()
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Invite team member</SheetTitle>
          <SheetDescription>
            Enter their email and choose a role. They will receive an invitation email to set up their account.
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
          <div className="grid gap-1.5">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              value={data.email}
              onChange={(e) => setData("email", e.target.value)}
              placeholder="colleague@example.com"
              required
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="role">Role</Label>
            <Select
              value={data.role}
              onValueChange={(v) => setData("role", v as MembershipRole)}
            >
              <SelectTrigger id="role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manager">Manager — can invite and remove team members</SelectItem>
                <SelectItem value="staff">Staff — view and manage property content</SelectItem>
              </SelectContent>
            </Select>
            {errors.role && <p className="text-xs text-destructive">{errors.role}</p>}
          </div>

          {/* user_id error is a uniqueness constraint — surface as a readable message */}
          {(errors.user_id || errors.base) && (
            <p className="text-xs text-destructive">
              {errors.user_id
                ? "This person is already a member of this property."
                : errors.base}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing || !data.email}>
              {processing ? "Sending invite…" : "Send invite"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}

function RoleBadge({ role }: { role: MembershipRole }) {
  return (
    <Badge variant={role === "manager" ? "default" : "outline"}>
      {role === "manager" ? "Manager" : "Staff"}
    </Badge>
  )
}

// C1: Added rolePending state + onFinish callback + error display
// I2: Role Select options include descriptions (consistent with InviteSheet)
function MemberRow({
  member,
  propertyName,
  canManage,
}: {
  member: PropertyMemberRow
  propertyName: string
  canManage: boolean
}) {
  const { errors } = usePage<SharedProps>().props
  const [rolePending, setRolePending] = useState(false)

  function handleRoleChange(newRole: string) {
    setRolePending(true)
    router.patch(
      member.update_role_url,
      { role: newRole },
      {
        preserveScroll: true,
        onFinish: () => setRolePending(false),
      }
    )
  }

  function handleRemove() {
    router.delete(member.remove_url, {
      preserveScroll: true,
    })
  }

  return (
    <TableRow>
      <TableCell className="font-medium">{member.name}</TableCell>
      <TableCell className="text-muted-foreground">{member.email}</TableCell>
      <TableCell>
        {canManage ? (
          <div className="space-y-1">
            <Select
              value={member.role}
              onValueChange={handleRoleChange}
              disabled={rolePending}
            >
              <SelectTrigger
                className="h-7 w-32 text-xs"
                aria-label={`Change role for ${member.name}`}
                aria-busy={rolePending}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manager">Manager — can invite and remove</SelectItem>
                <SelectItem value="staff">Staff — view and manage content</SelectItem>
              </SelectContent>
            </Select>
            {errors.role && (
              <p className="text-xs text-destructive">{errors.role}</p>
            )}
          </div>
        ) : (
          <RoleBadge role={member.role} />
        )}
      </TableCell>
      {/* I4: dateStyle for consistent locale rendering + title for ISO timestamp on hover */}
      <TableCell
        className="text-muted-foreground text-sm"
        title={member.created_at}
      >
        {new Date(member.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
      </TableCell>
      <TableCell>
        {canManage && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                aria-label={`Remove ${member.name}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove {member.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  They will immediately lose access to {propertyName}. You can re-invite them at any time.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleRemove}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </TableCell>
    </TableRow>
  )
}

export default function TeamIndex({ property, owner, members, can_manage_team, urls }: Props) {
  // C2: Key-increment pattern forces InviteSheet remount on open, clearing stale errors
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteKey, setInviteKey] = useState(0)

  function openInvite() {
    setInviteKey((k) => k + 1)
    setInviteOpen(true)
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Team</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{property.name}</p>
        </div>
        {can_manage_team && (
          <Button size="sm" className="gap-1.5" onClick={openInvite}>
            <UserPlus className="h-4 w-4" /> Invite Member
          </Button>
        )}
      </div>

      {/* Owner section */}
      <div className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground mb-3">Owner</h2>
        <div className="flex items-center gap-4 rounded-lg border p-4">
          <div className="flex-1">
            <p className="font-medium">{owner.name}</p>
            <p className="text-sm text-muted-foreground">{owner.email}</p>
          </div>
          <Badge variant="secondary">Owner</Badge>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Ownership transfer requires platform admin.
        </p>
      </div>

      {/* Team members */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground mb-3">
          Team members {members.length > 0 && `(${members.length})`}
        </h2>

        {members.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <p className="text-sm text-muted-foreground">No team members yet.</p>
            {/* I3: Differentiated guidance for staff vs manager in empty state */}
            {can_manage_team ? (
              <Button
                variant="outline"
                size="sm"
                className="mt-3 gap-1.5"
                onClick={openInvite}
              >
                <UserPlus className="h-4 w-4" /> Invite your first member
              </Button>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">
                Contact the property owner or a manager to add team members.
              </p>
            )}
          </div>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    propertyName={property.name}
                    canManage={can_manage_team}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <InviteSheet
        key={inviteKey}
        inviteUrl={urls.invite}
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />
    </div>
  )
}

TeamIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
